/**
 * @ngdoc service
 * @name BaseClass.BCBelongsToMany
 * @description
 * Model extension for REST functionality
 */
export default angular.module('BaseClass.belongsToMany',[])
    .factory('BCBelongsToMany', function($q, $injector, $http, $filter) {
    /**
     * Template functions for querying belongsToMany relationships. These
     * functions will be added to the prototype of a Model.
     */
        var childRelationRestFn = {
        /*
         * @description
         * Hidden function to interpolate an URL. Replace ^attr with values
         * from the child model, and :attr with values from the instance.
         * @example /api/tickets/:guid/products/^guid
         */
            _compileUrl: function(url, instance, child = {}) {
                var finalUrl = url,
                    ir = /\:([^\/]+)/g,
                    cr = /\^([^\/]+)/g;
                let instanceParams = ir.exec(url),
                    childParams = cr.exec(url);
                while(instanceParams !== null) {
                    finalUrl = finalUrl.replace(instanceParams[0], instance[instanceParams[1]] || '');
                    instanceParams = ir.exec(url);
                }
                while(childParams !== null) {
                    finalUrl = finalUrl.replace(childParams[0], child[childParams[1]] || '');
                    childParams = cr.exec(url);
                }

                return finalUrl.replace(/\/$/,'');
            },
            /*
             * Get all the associated models
             */
            query: function(modelName, url, instance, cacheOnly = false){
                if(instance.belongsToMany[modelName] || cacheOnly)
                    return $q.resolve(instance.belongsToMany.restore(modelName));
                var Model = $injector.get(modelName);
                return $http.get(url).then(response => {
                    instance.broadcastAPIMessage(response);
                    var models = _.map(response.data, m => {
                        m = Model.new(m);
                        instance.belongsToMany.cache(modelName, m);
                        return m;
                    });
                    return $q.resolve(models);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            },
            /*
             * Update the pivot table information for a child
             */
            update: function(modelName, url, instance, child, pivot){
                pivot = pivot || child.pivot;
                return $http.put(url, child.pivot).then(response => {
                    instance.broadcastAPIMessage(response);
                    return $q.resolve(instance);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            },
            /*
             * Detach a child from this instance
             */
            detach: function(modelName, url, instance, child){
                return $http.delete(url).then( response => {
                    instance.broadcastAPIMessage(response);
                    instance.belongsToMany.remove(modelName, child);
                    child.belongsToMany.remove(instance.constructor.modelName, instance);
                    return $q.resolve(instance);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            },
            /*
             * Attach a child to this instance
             */
            attach: function(modelName, url, instance, child, pivot){
                return $http.post(url, pivot).then( response => {
                    instance.broadcastAPIMessage(response);
                    instance.belongsToMany.cache(modelName, child, pivot);
                    if(child.belongsToMany[instance.constructor.modelName])
                        child.belongsToMany.cache(instance.constructor.modelName, instance, pivot);
                    return $q.resolve(instance);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });

            },
            /*
             * Delete and detach a child from this instance
             */
            delete: function(modelName, url, instance, child){
                var Model = $injector.get(modelName);
                return $http.delete(url).then( response => {
                    instance.broadcastAPIMessage(response);
                    Model.cached.remove(child[child.constructor.primaryKey]);
                    instance.belongsToMany.remove(modelName, child);
                    return $q.resolve(child);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            },
            /*
             * Create and attach a child to this instance
             */
            create: function(modelName, url, instance, child, pivot){
                var Model = $injector.get(modelName);
                return $http.post(url, child).then( response => {
                    instance.broadcastAPIMessage(response);
                    child = Model.new(child);
                    instance.belongsToMany.cache(modelName, child, pivot);
                    child.belongsToMany.cache(instance.constructor.modelName, instance, pivot);
                    return $q.resolve(child);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            },
            /**
             * Reorder products in the linked list
             *
             * We're only moving predecessor. So if you want to move a product to the end of the list, make two
             * calls. One to move product to `end-1` and then to swap the last two items around.
             *
             * @param {String} modelName `Product`
             * @param {String} url The compiled url for API endpoint
             * @param {Ticket} parent The parent that sortable objects belong to
             * @param {Product} predecessor The left side of the new pair
             * @param {Product} successor The right side of the new pair
             * @returns {Product[]} Promise that resolves with all sortables, in their correct order. It sorts and
             * modifies the array in place.
             */
            reorder: function(modelName, url, parent, predecessor, successor) {
                var children;
                var pk = successor.constructor.primaryKey;
                return parent['$query' + modelName]().then(list => {
                    children = list;

                    return $http.put(url, {
                        predecessor: _.get(predecessor, pk, null),
                        successor: _.get(successor, pk)
                    });
                });
            }
        };

        function BelongsToMany() {
            /**
             * @ngdoc function
             * @name BaseClass.BCBelongsToMany#belongsToMany
             * @methodOf BaseClass.BCBelongsToMany
             * @description
             * Will add actions to a model's prototype and parse nested models. (i.e.:
             * server responds with nested models, it'll pick out the EventDates)
             * <pre>
             * events: [{
             *     dates:[EventDates]
             * }]
             * </pre>
             *
             * View source file `common/base-class/restable/restable.js` for more information about the available preconfigured request types
             *
             * @example
             * <pre>
             * //The URL parameters are replaced with :local_attributes and ^foreignAttributes
             * Product.belongsToMany('MetaData', 'metaData', '/api/products/:guid/metaData/^guid', [
             *    { type: 'attach' }, // Attach an already existing child
             *    { type: 'detach' }, // Detach an existing child, don't delete the child
             *    { type: 'create' }, // Create and attach a child
             *    { type: 'delete' }, // Delete and detach a child
             *    { type: 'update' }, // Update pivot information
             *    { type: 'query' },  // Fetch all children
             *    {  // You can add custom methods, these options are available on all types
             *         type: 'custom',
             *         url: '/api/custom',
             *         fn: (modelName, url, instance, child, pivot)=>{},
             *         success: promiseResult=>{},
             *         error: promiseError=>{} }
             * ]);
             *
             * // Method signature: $ + type + {fnSuffix || modelName}
             * (Product.new()).$queryMetaData();
             * (Product.new()).$customMetaData();
             * </pre>
             *
             * @param {String} modelName Name of the child model Constructor (e.g.: User)
             * @param {String} propertyName Name of the property containing nested children (e.g.: users)
             * @param {String} url URL of child actions. (:attr will be replaced by parent properties, ^attr by child properties)
             * @param {Object[]} actions List of child functions
             * @param {String} fnSuffix The suffix for the prototype function, defaults to modelName (e.g.: $query[Suffix], $attach[Suffix])
             * @returns {this} Returns self
             */
            this.belongsToMany = function(modelName, propertyName, url, actions = [], fnSuffix = null){
                var Class = this,
                    Prototype = this.prototype;
                _.forEach(actions, action => {
                    var fn = action.fn || childRelationRestFn[action.type];
                    var fnName = '$' + action.type + (fnSuffix || modelName);
                    Object.defineProperty(Prototype, fnName, {
                        value: function(child, ...rest) {
                            var _url = childRelationRestFn._compileUrl(action.url || url, this, child);
                            var promise = fn.apply(Class, [modelName, _url, this, child, ...rest]);
                            if(action.success || action.error)
                                return promise.then(action.success, action.error);
                            return promise;
                        },
                        configurable: false,
                        enumerable: false
                    });
                });
                var oldConstructor = Class.new;
                Class.new = function(attributes) {
                    var instance = oldConstructor(attributes);
                    if(instance[propertyName]) {
                        instance.belongsToMany[modelName] = [];
                        var Child = $injector.get(modelName);
                        _.forEach(instance[propertyName], child => {
                            child = Child.new(child);
                            instance.belongsToMany.cache(modelName, child);
                        });
                        delete instance[propertyName];
                    }
                    return instance;
                };
                return Class;
            };

            /**
             * @ngdoc function
             * @name BaseClass.BCBelongsToMany#hasMany
             * @methodOf BaseClass.BCBelongsToMany
             * @description
             * Add a hasMany relationship.
             *
             * Will add query method to a model's prototype and parse nested models.
             * (i.e.: server responds with `{events:[{dates:[EventDates]}]}` it'll pick
             * out the EventDates)
             *
             * HasMany relationship is characterized by the child models containing
             * a `parent_id` attribute.
             *
             * @example
             * <pre>
             * Product.hasMany('MetaData', 'metaData', '/api/products/:guid/metaData/^guid');
             *
             * (Product.new()).$queryMetaData();
             * </pre>
             *
             * @param {String} modelName Name of the child model Constructor (e.g.: User)
             * @param {String} propertyName Name of the property containing nested children (e.g.: users)
             * @param {String} foreignKey Name of the property on the children that references parent
             * @param {String} url URL of child actions. (:attr will be replaced by parent properties, ^attr by child properties)
             * @param {String} fnSuffix The suffix for the prototype function, defaults to modelName (e.g.: $query[Suffix], $attach[Suffix])
             * @param {Function} successFn Function that will be executed on promise resolve before returning the promise
             * @param {Function} errorFn Function that will be executed on promise reject before returning the promise
             * @returns {this} Returns self
             */
            this.hasMany = function(modelName, propertyName, foreignKey, url, fnSuffix = null, successFn = null, errorFn = null) {
                var Class = this,
                    Prototype = this.prototype,
                    fnName = '$query' + (fnSuffix || modelName);
                Prototype[fnName] = function() {
                    var Child = $injector.get(modelName);
                    var query = {};
                    query[foreignKey] = this[Class.primaryKey];
                    if(Child.cachedQueries.isCached(query)) {
                        let children = Child.cached.where(query);
                        return $q.resolve(successFn ? successFn(children) : children);
                    }

                    let _url = childRelationRestFn._compileUrl(url, this);
                    return $http.get(_url).then(response => {
                        Class.broadcastAPIMessage(response);
                        var models = _.map(response.data, Child.new);
                        Child.cachedQueries.cache(query);
                        return successFn ? successFn(models) : $q.resolve(models);
                    }, error => {
                        Class.broadcastAPIMessage(error);
                        return errorFn ? errorFn(error) : $q.reject(error);
                    });
                };

                var oldConstructor = Class.new;
                Class.new = function(attributes) {
                    var instance = oldConstructor(attributes);
                    if(instance[propertyName]) {
                        var Child = $injector.get(modelName);
                        _.forEach(instance[propertyName], child => {
                            child = Child.new(child);
                        });
                        delete instance[propertyName];

                        var query = {};
                        query[foreignKey] = instance[Class.primaryKey];
                        Child.cachedQueries.cache(query);
                    }
                    return instance;
                };

                return Class;
            };

            /**
             * @ngdoc function
             * @name BaseClass.BCBelongsToMany#hasOne
             * @methodOf BaseClass.BCBelongsToMany
             * @description
             * Add a hasOne relationship. Similar to the hasMany, but expects a single child instead of on array.
             *
             * @example
             * <pre>
             * Product.hasOne('MetaData', 'metaData', '/api/products/:guid/metaData/^guid');
             *
             * (Product.new()).$getMetaData();
             * </pre>
             *
             * @param {String} modelName Name of the child model Constructor (e.g.: User)
             * @param {String} propertyName Name of the property containing nested children (e.g.: users)
             * @param {String} foreignKey Name of the property on the children that references parent
             * @param {String} url URL of child actions. (:attr will be replaced by parent properties, ^attr by child properties)
             * @param {Sting[]} fnSuffix The suffix for the prototype function, defaults to modelName (e.g.: $query[Suffix], $attach[Suffix])
             * @returns {this} Returns self
             */
            this.hasOne = function(modelName, propertyName, foreignKey, url, fnSuffix = null) {
                var Class = this,
                    Prototype = this.prototype,
                    fnName = '$get' + (fnSuffix || modelName);
                Prototype[fnName] = function() {
                    var Child = $injector.get(modelName);
                    var query = {};
                    query[foreignKey] = this[Class.primaryKey];
                    var cached = Child.cached.where(query);
                    if(cached && cached.length)
                        return $q.resolve(cached[0]);

                    let _url = childRelationRestFn._compileUrl(url, this);
                    return $http.get(_url).then(response => {
                        Class.broadcastAPIMessage(response);
                        var model = Child.new(response.data);
                        return $q.resolve(model);
                    }, error => {
                        Class.broadcastAPIMessage(error);
                        return $q.reject(error);
                    });
                };

                var oldConstructor = Class.new;
                Class.new = function(attributes) {
                    var instance = oldConstructor(attributes);
                    if(instance[propertyName]) {
                        var Child = $injector.get(modelName);
                        var child = Child.new(instance[propertyName]);
                        delete instance[propertyName];
                    }
                    return instance;
                };

                return Class;
            };

            /**
             * @ngdoc function
             * @name BaseClass.BCBelongsToMany#belongsTo
             * @methodOf BaseClass.BCBelongsToMany
             * @description
             * Add a belongsTo relationship.
             *
             * The inverse of has{Many,One} relationship. Adds a method for querying the
             * parent.
             *
             * @example
             * <pre>
             * Product.belongsTo('Category','category_id','guid','/api/category/^guid');
             *
             * (Product.new()).$getCategory();
             * </pre>
             *
             * @param {String} modelName Name of the parent model Constructor (e.g.: User)
             * @param {String} localKey Name of the attribute containing a reference to the parent
             * @param {String} foreignKey Name of the attribute on the parent
             * @param {String} url URL of child actions. (:attr will be replaced by parent properties, ^attr by child properties)
             * @param {Sting[]} fnSuffix The suffix for the prototype function, defaults to modelName (e.g.: $query[Suffix], $attach[Suffix])
             * @return {this} Returns self
             */
            this.belongsTo = function(modelName, localKey, foreignKey, url, fnSuffix = null){
                var Class = this,
                    Prototype = this.prototype,
                    fnName = '$get' + (fnSuffix || modelName);
                Prototype[fnName] = function() {
                    var urlParams = {};
                    urlParams[foreignKey] = this[localKey];
                    let _url = childRelationRestFn._compileUrl(url, this, urlParams);
                    return $http.get(_url).then(response => {
                        Class.broadcastAPIMessage(response);
                        var Parent = $injector.get(modelName);
                        return $q.resolve(Parent.new(response.data));
                    }, error => {
                        Class.broadcastAPIMessage(error);
                        return $q.reject(error);
                    });
                };
                return Class;
            };
        }

        return BelongsToMany;
    }).name;
